/**
 * Curve editor feature module (W2-04b curve-editor; DESIGN.md §4.7, §5.6.2-5.6.3, §5.6.7; ARCHITECTURE.md §7.7;
 * WORKPACKAGES Appendix B: `curve.*`).
 *
 * Provides:
 *  - column def `motion-expanded` (curveColumn.ts): the expanded graph of one motion column (single or channel
 *    view, speed view for Scroll speed) next to the motion columns, while `timeline.expandedColumn` is set;
 *  - commands `curve.*` (curveCommands.ts), `G` in both presets;
 *  - settings rows `ext.curve-editor.{sigma,ghosts,distance,channel}` (switches that read through the editor's
 *    defaults: CurveSettingRow.tsx), a help topic, the `curve.expanded` atom;
 *  - `init`: relayout when the expanded column, the level, the layout, the active line, the channel preference or
 *    the way the column is shown (single, channel or pair view) changes; the editor follows the active line (same
 *    property and layer on the new line); it closes when its line is deleted.
 */

import type { AppServices, FeatureModule, HelpTopicDef, SettingDef } from '../../app/registry/types';
import type { Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { ExperienceLevel } from '../../state/types';
import { asTimelineHostController } from '../../render/timeline/TimelineHost';
import { collapseEditor, expandedTarget } from './curveActions';
import { createCurveColumnDef, curvePlacement } from './curveColumn';
import { CURVE_COMMANDS, CURVE_KEYBINDINGS } from './curveCommands';
import { m } from './curveEditor.messages';
import { FEATURE_ID, isLayered, layerCount, motionColumnId, parseMotionColumnId } from './curveModel';
import { curveSettingSwitch } from './CurveSettingRow';
import { curveSettingPath, type CurveSettingKey } from './curveSettings';

function settingRow(
  key: CurveSettingKey,
  title: MessageKey,
  description: MessageKey,
  level: ExperienceLevel,
): SettingDef {
  return {
    path: curveSettingPath(key),
    title,
    description,
    level,
    kind: 'custom',
    component: curveSettingSwitch(key, title),
  };
}

const SETTINGS: readonly SettingDef[] = [
  settingRow('sigma', m.settingSigma, m.settingSigmaHelp, 'full'),
  settingRow('ghosts', m.settingGhosts, m.settingGhostsHelp, 'full'),
  settingRow('distance', m.settingDistance, m.settingDistanceHelp, 'guided'),
  settingRow('channel', m.settingChannel, m.settingChannelHelp, 'full'),
];

const HELP: readonly HelpTopicDef[] = [
  { id: 'curve-editor', title: m.helpTitle, body: m.helpBody, level: 'guided', showMe: { helpId: 'curve-editor' } },
];

/** Keeps the timeline layout in step with the editor state (see the module comment). Returns the disposer. */
export function wireCurveEditor(s: AppServices): () => void {
  let storeUnsub: Unsubscribe | null = null;

  /** Instance id and default width of the editor as last laid out (null while closed). */
  let shown: string | null = null;
  const placementKey = (): string | null => {
    const target = expandedTarget(s);
    if (target === null) return null;
    const p = curvePlacement(s, target);
    return `${p.id}|${String(p.widthPx)}`;
  };
  const relayout = (): void => {
    shown = placementKey();
    s.timeline?.relayout();
  };
  const reveal = (): void => {
    const target = expandedTarget(s);
    const host = s.timeline === null ? null : asTimelineHostController(s.timeline);
    if (target !== null && host !== null) host.revealColumn(curvePlacement(s, target).id);
  };
  /**
   * Relayout when the editor would now be shown differently: another view (the channel preference, a line that
   * became an image line and so has a Size pair) or another default width (layers added or flattened in the channel
   * view).
   */
  const relayoutIfMoved = (): void => {
    if (placementKey() !== shown) relayout();
  };

  const closeIfGone = (): void => {
    const ref = parseMotionColumnId(s.editor.getState().timeline.expandedColumn);
    const store = s.store;
    if (ref !== null && store !== null && !store.doc.lines.has(ref.line)) collapseEditor(s);
  };

  const wireStore = (): void => {
    storeUnsub?.();
    storeUnsub =
      s.store?.subscribe((cs) => {
        if (cs.dirty.lineSet) closeIfGone();
        relayoutIfMoved();
      }) ?? null;
  };

  const editorUnsub = s.editor.subscribe((next, prev) => {
    const expanded = next.timeline.expandedColumn;
    // Follow the active line: the same property (and layer, when it exists) on the new line.
    if (expanded !== null && next.activeLine !== prev.activeLine && next.activeLine !== null) {
      const ref = parseMotionColumnId(expanded);
      const store = s.store;
      if (ref !== null && store !== null && ref.line !== next.activeLine && store.doc.lines.has(next.activeLine)) {
        const count = layerCount(store.doc, store.idx, next.activeLine, ref.prop);
        const layer = isLayered(ref.prop) && ref.layer < count ? ref.layer : 0;
        next.setTimeline({ expandedColumn: motionColumnId({ line: next.activeLine, prop: ref.prop, layer }) });
        return;
      }
    }
    if (next.timeline.expandedColumn !== prev.timeline.expandedColumn) {
      relayout();
      reveal();
    } else if (
      expanded !== null &&
      (next.level !== prev.level || next.layout !== prev.layout || next.activeLine !== prev.activeLine)
    ) {
      relayout();
    }
  });

  // The channel preference changes how the editor is shown (its instance id).
  const settingsUnsub = s.settings.subscribe(() => {
    if (s.editor.getState().timeline.expandedColumn !== null) relayoutIfMoved();
  });

  shown = placementKey();
  wireStore();
  const changeUnsub = s.onChange(() => {
    wireStore();
    closeIfGone();
    relayoutIfMoved();
  });

  return () => {
    editorUnsub();
    settingsUnsub();
    changeUnsub();
    storeUnsub?.();
  };
}

export default {
  id: FEATURE_ID,
  commands: CURVE_COMMANDS,
  keybindings: CURVE_KEYBINDINGS,
  settings: SETTINGS,
  helpTopics: HELP,
  timelineColumns: (s) => [createCurveColumnDef(s)],
  whenAtoms: (s) => ({ 'curve.expanded': () => expandedTarget(s) !== null }),
  init: (s) => wireCurveEditor(s),
} satisfies FeatureModule;
